import React, { useContext } from 'react';

import { useAuth } from 'platform/auth-fe';
import { useObservability } from 'platform/observability';
import { usePageStalenessTimer } from 'platform/useReloadStalePage';

export interface ReauthOptions {
  /** Called if reauth was successful. */
  onSuccess: () => void;
  /** Called if reauth was canceled. */
  onCancel: () => void;
  /**
   * Title of reauth UI.
   * Default: "Sign in again to continue"
   */
  title?: string;
}

export interface ReauthResult {
  showReauth: (options: ReauthOptions) => void;
}

/** Context for global reauth functionality. */
export const ReauthContext = React.createContext<ReauthResult>({
  showReauth: () => {
    throw new Error('missing ReauthContext provider');
  },
});

/** Hook to access global reauth functionality. */
export const useReauth = () => {
  return useContext(ReauthContext);
};

/**
 * @returns Function to track + refresh auth state
 */
export const useRefreshAuthState = () => {
  const { trackEvent } = useObservability();
  const { authValidatedTime } = usePageStalenessTimer();
  const authentication = useAuth();

  return () => {
    trackEvent(
      'stale-auth-revalidate',
      'first-navigation-after-window',
      { timeSinceAuthValidatedMs: Date.now() - authValidatedTime },
      // Adds a custom datadog time event in the user's session
      { addTiming: true },
    );
    authentication.refresh();
  };
};
